import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateClosingPeriodDocument,
  SignedInvoicesPageDocument,
  SignedInvoicesPageQuery,
} from 'generated/resources/graphql'

type ExpensesPendingClosingPeriod = NonNullable<
  NonNullable<SignedInvoicesPageQuery['expenses']['pendingClosingPeriodsConnection']>['nodes']
>[number]

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { monthPeriodToMonthYear, quarterPeriodToMonthYear } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface ClosingPeriodModalParams {
  legalEntityId: number
  aeatClosingPeriodFrequency: string
  setOpenModal: (value: boolean) => void
  closingPeriodsPending?: ExpensesPendingClosingPeriod[]
}

const ClosingPeriodModal = ({
  closingPeriodsPending,
  legalEntityId,
  aeatClosingPeriodFrequency,
  setOpenModal,
}: ClosingPeriodModalParams) => {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | undefined>(undefined)
  const { company } = useCurrentSession()

  const onClose = () => {
    setOpenModal(false)
  }

  const [createClosingPeriod, { loading: submitting }] = useMutation(CreateClosingPeriodDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesClosingPeriod,
  })

  const onSubmit = async () => {
    const pendingClosingPeriod = closingPeriodsPending?.find(
      (period) => period.id === selectedPeriodId
    )

    if (!pendingClosingPeriod) {
      return
    }

    const result = await createClosingPeriod({
      variables: {
        year: pendingClosingPeriod.year,
        period: pendingClosingPeriod.period,
        legalEntityId: legalEntityId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: SignedInvoicesPageDocument,
          variables: {
            companyId: company.id,
            legalEntityId: legalEntityId,
          },
        },
      ],
    })

    const handled = handleResult(result, (response) => {
      if (response.messages) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('expenses.settings.signed_invoices.alerts.period_closed_error'),
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses.settings.signed_invoices.alerts.period_closed'),
      })

      onClose()
    }
  }

  const onChangeEvent = (periodId: string) => {
    setSelectedPeriodId(periodId)
  }

  const periodLabel = (periodData: ExpensesPendingClosingPeriod) => {
    switch (aeatClosingPeriodFrequency) {
      case 'quarterly':
        return quarterPeriodToMonthYear(periodData.period, periodData.year)
      case 'monthly':
        return monthPeriodToMonthYear(periodData.period, periodData.year)
      default:
        return ''
    }
  }

  const periodValue = (periodData: ExpensesPendingClosingPeriod) => {
    return periodData.id
  }

  const optionsForSelect = () => {
    if (!closingPeriodsPending) {
      return []
    }

    return closingPeriodsPending.map((periodData: ExpensesPendingClosingPeriod) => ({
      label: periodLabel(periodData),
      value: periodValue(periodData),
    }))
  }

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('expenses.settings.signed_invoices.modal.closing_period.title')}
      description={i18n.t('expenses.settings.signed_invoices.modal.closing_period.description')}
      onClose={() => onClose()}
      onSubmit={onSubmit}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('expenses.settings.signed_invoices.modal.cancel')}
          </Button>
          <Button disabled={!selectedPeriodId || submitting} submit>
            {i18n.t('expenses.settings.signed_invoices.modal.closing_period.submit')}
          </Button>
        </Box>
      }
    >
      <SelectField
        mandatory
        label={i18n.t('expenses.settings.signed_invoices.modal.closing_period.select')}
        options={optionsForSelect()}
        onChange={onChangeEvent}
        value={selectedPeriodId}
        none={i18n.t('common.choose')}
      />
    </Modal>
  )
}

export default ClosingPeriodModal
